import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useUser } from '../../app/providers/userContext';
import { useAppState } from '../../app/providers/appStateContext';
import { setLoginTime, setUserData } from '../../app/store/slices/userSlice';
import { unregisterFCMToken } from '../../app/config/firebase/pushNotifications';
import { cancelAllRequests } from '../../services/api/axiosInstance';
import { isSubscriptionRestricted, canRenewSubscription } from '../constants/subscriptionAccess';
import {
  getAccountId,
  getSavedAccounts,
  isTokenExpired,
  removeAccount,
  syncActiveAccountToken,
  saveAccount,
} from '../utils/savedAccounts';

const ROLE_DASHBOARD = {
  TEACHER: '/teacher/dashboard',
  PRINCIPAL: '/admin/dashboard',
  SCHOOL_ADMIN: '/admin/dashboard',
  ADMIN: '/admin/dashboard',
  STUDENT: '/student/dashboard',
  SUPER_ADMIN: '/super_admin/dashboard',
};

export const toReduxUser = (userData) => ({
  schoolId: userData.schoolId,
  schoolName: userData.schoolName,
  schoolCode: userData.schoolCode,
  schoolLogo: userData.schoolLogo,
  role: userData.roles?.[0],
  email: userData.email,
  username: userData.username,
  name: userData.name,
  permissions: userData.permission,
  subscriptionStatus: userData.subscriptionStatus,
  trialEndsIn: userData.trialEndsIn,
  googleDriveConnected: userData.googleDriveConnected,
  isOnboardComplete: userData.isOnboardComplete,
});

export function useAccountSwitcher() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user, login, logout } = useUser();
  const { reset } = useAppState();
  const [accounts, setAccounts] = useState(getSavedAccounts);
  const [switching, setSwitching] = useState(false);
  const pending = useRef(false);

  useEffect(() => {
    const refresh = () => setAccounts(getSavedAccounts());
    window.addEventListener('saved-accounts:changed', refresh);
    return () => window.removeEventListener('saved-accounts:changed', refresh);
  }, []);

  const activeId = user ? getAccountId(user) : null;

  const activateProfile = useCallback(async (userData, token) => {
    if (pending.current) return;
    const role = userData?.roles?.[0];
    if (!ROLE_DASHBOARD[role] || typeof token !== 'string' || !token.trim()) {
      throw new Error('This profile does not have a valid session or supported role.');
    }
    pending.current = true;
    setSwitching(true);
    try {
      syncActiveAccountToken();
      await unregisterFCMToken();
      cancelAllRequests();
      localStorage.setItem('token', token);
      localStorage.setItem('auth_role', role);
      localStorage.setItem('user_id', userData.username || userData.email || '');
      saveAccount(userData, token);
      dispatch(setLoginTime(Date.now()));
      dispatch(setUserData(toReduxUser(userData)));
      reset();
      login({ ...userData, token });
      const target = isSubscriptionRestricted(userData) && canRenewSubscription(userData)
        ? '/admin/setup/pricing' : ROLE_DASHBOARD[role];
      // A new document also discards module-level caches belonging to the outgoing student.
      window.location.replace(target);
    } finally {
      pending.current = false;
      setSwitching(false);
    }
  }, [dispatch, login, reset]);

  // Ends the active session but keeps every saved profile, then opens the login screen to add another one.
  const addAccount = useCallback(async () => {
    syncActiveAccountToken();
    await unregisterFCMToken();
    logout();
    reset();
    localStorage.removeItem('user_id');
    navigate('/login', { replace: true, state: { addAccount: true } });
  }, [logout, reset, navigate]);

  const switchTo = useCallback(
    async (account) => {
      if (switching || !account || account.id === activeId) return;

      if (isTokenExpired(account.token)) {
        // The saved login is no longer valid; ask for the password again with the username prefilled.
        await unregisterFCMToken();
        logout();
        reset();
        localStorage.removeItem('user_id');
        navigate('/login', {
          replace: true,
          state: { addAccount: true, username: account.userData?.username },
        });
        return;
      }

      await activateProfile(account.userData, account.token);
    },
    [switching, activeId, reset, navigate, logout, activateProfile]
  );

  return { accounts, activeId, switching, switchTo, activateProfile, addAccount, removeAccount };
}
